import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Clock, MapPin,
         User, Home, Check, Loader2, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// El calendario de la organización: visitas y reuniones.
//
// La fecha de visita ya vivía en un campo de la ficha del vendedor, pero un
// campo suelto no es una agenda: no deja ver qué hay el jueves, no guarda la
// hora y el comprador ni siquiera lo tenía. Aquí se ven todos juntos.
//
// Lo que se agenda desde la ficha (poner un vendedor en "visita agendada" con
// su día) aparece aquí solo, sin tener que apuntarlo dos veces.

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
               'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

const COLOR_TIPO = {
  visita:  { fondo: 'rgba(201,168,76,0.18)',  borde: '#c9a84c', texto: '#e6c86e' },
  reunion: { fondo: 'rgba(6,182,212,0.18)',   borde: '#06b6d4', texto: '#67e8f9' },
  llamada: { fondo: 'rgba(139,92,246,0.18)',  borde: '#8b5cf6', texto: '#c4b5fd' },
  otro:    { fondo: 'rgba(100,116,139,0.18)', borde: '#64748b', texto: '#cbd5e1' },
};
const ICONO_ENTIDAD = { seller: <Home size={11} />, buyer: <User size={11} />, professional: <User size={11} /> };
const NOMBRE_ENTIDAD = { seller: 'Vendedor', buyer: 'Comprador', professional: 'Profesional' };

// Clave local YYYY-MM-DD. OJO: nada de toISOString(), que pasa a UTC y en
// España manda las visitas de después de las 22:00 al día siguiente.
const clave = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hhmm = (iso) => new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

export function Calendario({ orgId }) {
  const [mes, setMes] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [eventos, setEventos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [ficha, setFicha] = useState(null);      // el evento que se está editando o creando
  const [guardando, setGuardando] = useState(false);
  const [vendedores, setVendedores] = useState([]);
  const [compradores, setCompradores] = useState([]);

  const cargar = useCallback(async () => {
    if (!orgId) return;
    // Un mes de margen por cada lado: así al cambiar de mes ya está cargado.
    const desde = new Date(mes.getFullYear(), mes.getMonth() - 1, 1);
    const hasta = new Date(mes.getFullYear(), mes.getMonth() + 2, 0);
    try {
      const [e, v, c] = await Promise.all([
        axios.get(`${API}/crm/eventos?org_id=${orgId}&desde=${clave(desde)}&hasta=${clave(hasta)}`),
        axios.get(`${API}/crm/sellers?org_id=${orgId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/crm/buyers?org_id=${orgId}`).catch(() => ({ data: [] })),
      ]);
      setEventos(e.data || []);
      setVendedores(v.data || []);
      setCompradores(c.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar el calendario');
    }
    setCargando(false);
  }, [orgId, mes]);

  useEffect(() => { cargar(); }, [cargar]);

  // Los eventos agrupados por día, para no recorrer la lista en cada celda.
  const porDia = useMemo(() => {
    const m = {};
    for (const ev of eventos) {
      const k = clave(new Date(ev.inicio));
      (m[k] = m[k] || []).push(ev);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => new Date(a.inicio) - new Date(b.inicio));
    return m;
  }, [eventos]);

  // La rejilla del mes, empezando en lunes como aquí.
  const celdas = useMemo(() => {
    const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
    const hueco = (primero.getDay() + 6) % 7;          // domingo=0 → lunes=0
    const dias = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < hueco; i++) out.push(null);
    for (let d = 1; d <= dias; d++) out.push(new Date(mes.getFullYear(), mes.getMonth(), d));
    while (out.length % 7) out.push(null);
    return out;
  }, [mes]);

  const proximos = useMemo(() => {
    const ahora = Date.now();
    return eventos
      .filter((e) => e.estado === 'pendiente' && new Date(e.inicio).getTime() > ahora - 2 * 3600e3)
      .sort((a, b) => new Date(a.inicio) - new Date(b.inicio))
      .slice(0, 8);
  }, [eventos]);

  const nuevoEn = (dia) => {
    const d = dia || new Date();
    setFicha({
      titulo: '', tipo: 'visita',
      fecha: clave(d), hora: '10:00', duracion: 60,
      entidad_tipo: '', entidad_id: '', lugar: '', notas: '',
    });
  };

  const abrir = (ev) => {
    const d = new Date(ev.inicio);
    setFicha({
      evento_id: ev.evento_id,
      titulo: ev.titulo, tipo: ev.tipo,
      fecha: clave(d),
      hora: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      duracion: ev.fin ? Math.max(15, Math.round((new Date(ev.fin) - d) / 60000)) : 60,
      entidad_tipo: ev.entidad_tipo || '', entidad_id: ev.entidad_id || '',
      lugar: ev.lugar || '', notas: ev.notas || '', estado: ev.estado,
    });
  };

  const guardar = async () => {
    if (!ficha.titulo.trim()) { toast.error('Ponle un título'); return; }
    setGuardando(true);
    const inicio = `${ficha.fecha}T${ficha.hora}`;
    const fin = new Date(new Date(inicio).getTime() + (ficha.duracion || 60) * 60000);
    const finTxt = `${clave(fin)}T${String(fin.getHours()).padStart(2, '0')}:${String(fin.getMinutes()).padStart(2, '0')}`;
    const lista = ficha.entidad_tipo === 'seller' ? vendedores : compradores;
    const elegido = lista.find((x) => (x.seller_id || x.buyer_id) === ficha.entidad_id);
    const cuerpo = {
      org_id: orgId,
      titulo: ficha.titulo.trim(), tipo: ficha.tipo,
      inicio, fin: finTxt,
      entidad_tipo: ficha.entidad_tipo || null,
      entidad_id: ficha.entidad_id || null,
      entidad_nombre: elegido ? (elegido.nombre_propietario || elegido.nombre) : null,
      lugar: ficha.lugar || null, notas: ficha.notas || null,
    };
    try {
      if (ficha.evento_id) {
        await axios.put(`${API}/crm/eventos/${ficha.evento_id}`, { ...cuerpo, estado: ficha.estado });
        toast.success('Evento actualizado');
      } else {
        await axios.post(`${API}/crm/eventos`, cuerpo);
        toast.success('Evento creado');
      }
      setFicha(null); cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  const marcarHecha = async (ev) => {
    try {
      await axios.put(`${API}/crm/eventos/${ev.evento_id}`, { org_id: orgId, estado: 'hecha' });
      toast.success('Marcada como hecha'); cargar();
    } catch (err) { toast.error('No se pudo marcar'); }
  };

  const borrar = async () => {
    if (!ficha?.evento_id) { setFicha(null); return; }
    try {
      await axios.delete(`${API}/crm/eventos/${ficha.evento_id}?org_id=${orgId}`);
      toast.success('Evento eliminado'); setFicha(null); cargar();
    } catch (err) { toast.error('No se pudo eliminar'); }
  };

  if (cargando) return <div className="p-8 text-white/40 text-sm">Cargando el calendario…</div>;

  const hoy = clave(new Date());

  return (
    <div className="space-y-5">
      {/* Cabecera con el mes */}
      <div className="flex items-center gap-3">
        <button onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}
          className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/6">
          <ChevronLeft size={16} />
        </button>
        <h3 className="text-lg font-black text-white capitalize min-w-[190px] text-center">
          {MESES[mes.getMonth()]} {mes.getFullYear()}
        </h3>
        <button onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}
          className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/6">
          <ChevronRight size={16} />
        </button>
        <button onClick={() => { const d = new Date(); d.setDate(1); setMes(d); }}
          className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white/50 hover:text-white bg-white/4">
          Hoy
        </button>
        <button onClick={() => nuevoEn(null)}
          className="ml-auto px-4 py-2 rounded-lg text-[11px] font-bold bg-[#c9a84c]/20 text-[#c9a84c] hover:bg-[#c9a84c]/30 flex items-center gap-1.5">
          <Plus size={13} /> Nuevo evento
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_290px] gap-5">
        {/* La rejilla */}
        <div className="rounded-xl border border-white/8 overflow-hidden">
          <div className="grid grid-cols-7 bg-white/4">
            {DIAS.map((d) => (
              <div key={d} className="p-2 text-center text-[10px] font-bold text-white/40 uppercase">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {celdas.map((dia, i) => {
              const k = dia ? clave(dia) : null;
              const delDia = k ? (porDia[k] || []) : [];
              const esHoy = k === hoy;
              return (
                <div key={i}
                  onClick={() => dia && nuevoEn(dia)}
                  className={`min-h-[92px] p-1.5 border-t border-r border-white/6 ${dia ? 'cursor-pointer hover:bg-white/4' : 'bg-black/20'}`}>
                  {dia && (
                    <>
                      <div className={`text-[11px] font-bold mb-1 ${esHoy ? 'text-black bg-[#c9a84c] rounded-full w-5 h-5 flex items-center justify-center' : 'text-white/40'}`}>
                        {dia.getDate()}
                      </div>
                      <div className="space-y-1">
                        {delDia.slice(0, 3).map((ev) => {
                          const c = COLOR_TIPO[ev.tipo] || COLOR_TIPO.otro;
                          const tachado = ev.estado !== 'pendiente';
                          return (
                            <div key={ev.evento_id}
                              onClick={(e) => { e.stopPropagation(); abrir(ev); }}
                              title={`${hhmm(ev.inicio)} · ${ev.titulo}`}
                              className="px-1.5 py-0.5 rounded text-[9.5px] font-semibold truncate border-l-2"
                              style={{ background: c.fondo, borderColor: c.borde, color: c.texto,
                                       opacity: tachado ? 0.45 : 1,
                                       textDecoration: ev.estado === 'cancelada' ? 'line-through' : 'none' }}>
                              {hhmm(ev.inicio)} {ev.titulo}
                            </div>
                          );
                        })}
                        {delDia.length > 3 && (
                          <div className="text-[9px] text-white/30 pl-1">+{delDia.length - 3} más</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Lo que viene */}
        <div className="rounded-xl border border-white/8 p-4">
          <p className="text-white/40 text-[11px] uppercase tracking-wide flex items-center gap-1.5 mb-3">
            <CalendarDays size={12} /> Lo próximo
          </p>
          {proximos.length === 0 ? (
            <p className="text-white/30 text-xs">Nada pendiente. Pincha en un día para apuntar algo.</p>
          ) : (
            <div className="space-y-2">
              {proximos.map((ev) => {
                const c = COLOR_TIPO[ev.tipo] || COLOR_TIPO.otro;
                const d = new Date(ev.inicio);
                return (
                  <div key={ev.evento_id} onClick={() => abrir(ev)}
                    className="rounded-lg p-2.5 cursor-pointer border-l-2 hover:bg-white/4"
                    style={{ background: c.fondo, borderColor: c.borde }}>
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-bold truncate" style={{ color: c.texto }}>{ev.titulo}</p>
                        <p className="text-white/40 text-[10px] mt-0.5 flex items-center gap-1">
                          <Clock size={9} />
                          {d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })} · {hhmm(ev.inicio)}
                        </p>
                        {ev.entidad_tipo && (
                          <p className="text-white/30 text-[10px] mt-0.5 flex items-center gap-1">
                            {ICONO_ENTIDAD[ev.entidad_tipo]} {ev.entidad_nombre || NOMBRE_ENTIDAD[ev.entidad_tipo]}
                          </p>
                        )}
                        {ev.propiedad_texto && (
                          <p className="text-white/30 text-[10px] mt-0.5 truncate">{ev.propiedad_texto}</p>
                        )}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); marcarHecha(ev); }}
                        title="Marcar como hecha"
                        className="p-1 rounded text-white/30 hover:text-emerald-400 shrink-0">
                        <Check size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* La ficha del evento */}
      {ficha && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setFicha(null)}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0f1115] p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-black">{ficha.evento_id ? 'Editar evento' : 'Nuevo evento'}</h4>
              <button onClick={() => setFicha(null)} className="text-white/40 hover:text-white p-1"><X size={18} /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide">Título</label>
                <input value={ficha.titulo} onChange={(e) => setFicha({ ...ficha, titulo: e.target.value })}
                  placeholder="Visita con..." autoFocus
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#c9a84c]/50" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/40 text-[10px] uppercase tracking-wide">Tipo</label>
                  <select value={ficha.tipo} onChange={(e) => setFicha({ ...ficha, tipo: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none">
                    <option value="visita" className="bg-[#0f1115]">Visita</option>
                    <option value="reunion" className="bg-[#0f1115]">Reunión</option>
                    <option value="llamada" className="bg-[#0f1115]">Llamada</option>
                    <option value="otro" className="bg-[#0f1115]">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="text-white/40 text-[10px] uppercase tracking-wide">Duración</label>
                  <select value={ficha.duracion} onChange={(e) => setFicha({ ...ficha, duracion: parseInt(e.target.value, 10) })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none">
                    {[30, 45, 60, 90, 120].map((m) => (
                      <option key={m} value={m} className="bg-[#0f1115]">{m} min</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/40 text-[10px] uppercase tracking-wide">Día</label>
                  <input type="date" value={ficha.fecha} onChange={(e) => setFicha({ ...ficha, fecha: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none" />
                </div>
                <div>
                  <label className="text-white/40 text-[10px] uppercase tracking-wide">Hora</label>
                  <input type="time" value={ficha.hora} onChange={(e) => setFicha({ ...ficha, hora: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/40 text-[10px] uppercase tracking-wide">Con quién</label>
                  <select value={ficha.entidad_tipo}
                    onChange={(e) => setFicha({ ...ficha, entidad_tipo: e.target.value, entidad_id: '' })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none">
                    <option value="" className="bg-[#0f1115]">Nadie en concreto</option>
                    <option value="seller" className="bg-[#0f1115]">Un vendedor</option>
                    <option value="buyer" className="bg-[#0f1115]">Un comprador</option>
                  </select>
                </div>
                <div>
                  <label className="text-white/40 text-[10px] uppercase tracking-wide">Ficha</label>
                  <select value={ficha.entidad_id} disabled={!ficha.entidad_tipo}
                    onChange={(e) => setFicha({ ...ficha, entidad_id: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none disabled:opacity-40">
                    <option value="" className="bg-[#0f1115]">—</option>
                    {(ficha.entidad_tipo === 'seller' ? vendedores : ficha.entidad_tipo === 'buyer' ? compradores : [])
                      .map((x) => {
                        const id = x.seller_id || x.buyer_id;
                        return <option key={id} value={id} className="bg-[#0f1115]">{x.nombre_propietario || x.nombre}</option>;
                      })}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide flex items-center gap-1">
                  <MapPin size={10} /> Dónde
                </label>
                <input value={ficha.lugar} onChange={(e) => setFicha({ ...ficha, lugar: e.target.value })}
                  placeholder="Dirección o sitio"
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#c9a84c]/50" />
              </div>

              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide">Notas</label>
                <textarea value={ficha.notas} onChange={(e) => setFicha({ ...ficha, notas: e.target.value })}
                  rows={2}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#c9a84c]/50 resize-none" />
              </div>

              {ficha.evento_id && (
                <div>
                  <label className="text-white/40 text-[10px] uppercase tracking-wide">Estado</label>
                  <select value={ficha.estado} onChange={(e) => setFicha({ ...ficha, estado: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none">
                    <option value="pendiente" className="bg-[#0f1115]">Pendiente</option>
                    <option value="hecha" className="bg-[#0f1115]">Hecha</option>
                    <option value="cancelada" className="bg-[#0f1115]">Cancelada</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 mt-5">
              {ficha.evento_id && (
                <button onClick={borrar}
                  className="p-2 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-500/10">
                  <Trash2 size={16} />
                </button>
              )}
              <button onClick={() => setFicha(null)}
                className="ml-auto px-4 py-2 rounded-lg text-sm font-semibold text-white/60 hover:text-white border border-white/10">
                Cancelar
              </button>
              <button onClick={guardar} disabled={guardando}
                className="px-5 py-2 rounded-lg text-sm font-bold bg-[#c9a84c] text-black hover:bg-[#d9b85c] disabled:opacity-40 flex items-center gap-1.5">
                {guardando && <Loader2 size={13} className="animate-spin" />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
